import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { queryClient } from "@/lib/queryClient";
import { secureApiRequest, clearCsrfToken } from "@/lib/csrfClient";
import { shutdownIntercom } from "@/lib/intercom";

// User interactions that count as "activity" and reset the idle timer.
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel",
];

// Throttle how often activity resets the timer to avoid excessive churn on
// high-frequency events like mousemove/scroll.
const RESET_THROTTLE_MS = 1000;

/**
 * Watches for user inactivity and force-logs-out the user once their configured
 * session timeout elapses. Without this, an idle user keeps seeing cached pages
 * (and only discovers they're logged out on a refresh). When the timer fires we
 * end the server session and hard-redirect to the login screen with a timeout
 * flag so the login page can show a dismissible "session expired" message.
 */
export function useIdleTimeout() {
  const { isAuthenticated, user } = useAuth();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loggingOutRef = useRef(false);

  // The org-enforced session timeout (minutes) is the single source of truth;
  // 0/undefined disables the timeout. It overrides any per-user value.
  const timeoutMinutes = user?.effectiveSessionTimeout ?? 0;

  useEffect(() => {
    if (!isAuthenticated || !timeoutMinutes || timeoutMinutes <= 0) {
      return;
    }

    const timeoutMs = timeoutMinutes * 60 * 1000;

    const handleTimeout = async () => {
      if (loggingOutRef.current) return;
      loggingOutRef.current = true;

      // End the server-side session. Use secureApiRequest directly (rather than
      // apiRequest) so we always control the final redirect and keep the
      // ?timeout=true flag even if the session is already gone server-side.
      try {
        await secureApiRequest("/api/auth/logout", { method: "POST" });
      } catch {
        // Ignore — the session may already be expired on the server.
      }

      try {
        shutdownIntercom();
      } catch {
        // Ignore Intercom teardown errors.
      }

      clearCsrfToken();
      queryClient.clear();
      window.location.href = "/auth?timeout=true";
    };

    const resetTimer = () => {
      if (loggingOutRef.current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(handleTimeout, timeoutMs);
    };

    let lastReset = Date.now();
    const onActivity = () => {
      const now = Date.now();
      if (now - lastReset < RESET_THROTTLE_MS) return;
      lastReset = now;
      resetTimer();
    };

    resetTimer();
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onActivity, { passive: true }),
    );

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, onActivity),
      );
    };
  }, [isAuthenticated, timeoutMinutes]);
}
